import { App, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Radio, Select, TimePicker, Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { createReminder, sendReminderNow, updateReminder } from '../api';
import {
  GalaReminder,
  ReminderAudience,
  ReminderInput,
  errorText,
  fromVnInput,
  groupMatches,
  toVnInput,
} from '../contract';
import styles from '../styles.module.scss';

const VN_FORMAT = 'YYYY-MM-DD HH:mm';
const PLACEHOLDER_HINT = 'Chèn được {title} (tên sự kiện), {days} (còn mấy ngày), {date} (ngày diễn ra), {name} (tên khách), {seat} (bàn, ghế — chỉ có sau khi bật "Cho khách xem sơ đồ").';

const AUDIENCE_OPTIONS: { value: ReminderAudience; label: string }[] = [
  { value: 'TICKET', label: 'Mọi người có vé' },
  { value: 'GROUPS', label: 'Chỉ một số nhóm khách' },
];

/** Dùng chung hai modal: tiêu đề, nội dung, đối tượng nhận (+ nhóm khi chọn "một số nhóm"). */
const MessageFields = ({ groupOptions }: { groupOptions: string[] }) => {
  const audience = Form.useWatch('audience');
  return (
    <>
      <Form.Item
        name="title"
        label="Tiêu đề"
        rules={[
          { required: true, whitespace: true, message: 'Nhập tiêu đề.' },
          { max: 120, message: 'Tối đa 120 ký tự.' },
        ]}
      >
        <Input maxLength={120} showCount />
      </Form.Item>
      <Form.Item
        name="body"
        label="Nội dung"
        extra={PLACEHOLDER_HINT}
        rules={[
          { required: true, whitespace: true, message: 'Nhập nội dung.' },
          { max: 300, message: 'Tối đa 300 ký tự.' },
        ]}
      >
        <Input.TextArea rows={3} maxLength={300} showCount />
      </Form.Item>
      <Form.Item name="audience" label="Gửi cho">
        <Select options={AUDIENCE_OPTIONS} />
      </Form.Item>
      {audience === 'GROUPS' && (
        <Form.Item
          name="groups"
          label="Nhóm khách"
          rules={[{ required: true, type: 'array', min: 1, message: 'Chọn ít nhất một nhóm.' }]}
          getValueFromEvent={(v: string[]) => v.map((g) => g.trim().toUpperCase()).filter(Boolean)}
        >
          <Select
            mode="tags"
            tokenSeparators={[',']}
            placeholder="Chọn hoặc gõ tên nhóm rồi Enter"
            options={groupOptions.map((g) => ({ value: g, label: g }))}
            filterOption={(input, opt) => groupMatches(input, String(opt?.value ?? ''))}
          />
        </Form.Item>
      )}
    </>
  );
};

interface ReminderModalProps {
  open: boolean;
  eventId: number;
  /** Có = sửa mốc này; null = thêm mốc. */
  editing: GalaReminder | null;
  groupOptions: string[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface ReminderValues {
  kind: 'OFFSET' | 'CUSTOM';
  days?: number;
  time?: Dayjs;
  at?: Dayjs;
  title: string;
  body: string;
  audience: ReminderAudience;
  groups?: string[];
}

const initialValues = (r: GalaReminder | null): Partial<ReminderValues> => {
  if (!r) return { kind: 'OFFSET', days: 7, time: dayjs('09:00', 'HH:mm'), audience: 'TICKET' };
  return {
    kind: r.kind === 'CUSTOM' ? 'CUSTOM' : 'OFFSET',
    days: Math.round((r.offsetMinutes ?? 0) / 1440),
    time: dayjs(r.sendTimeLocal || '09:00', 'HH:mm'),
    // Giờ VN hiểu theo giờ ghi trên đồng hồ, không phụ thuộc múi giờ máy admin.
    at: r.kind === 'CUSTOM' ? dayjs(toVnInput(r.sendAt), VN_FORMAT) : undefined,
    title: r.title,
    body: r.body,
    audience: r.audience,
    groups: r.groups ?? [],
  };
};

/** Thêm / sửa một mốc nhắc: OFFSET (N ngày trước, giờ HH:mm) hoặc CUSTOM (ngày giờ cố định, giờ VN). */
export const ReminderModal = ({ open, eventId, editing, groupOptions, onClose, onSaved }: ReminderModalProps) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<ReminderValues>();
  const kind = Form.useWatch('kind', form);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    let v: ReminderValues;
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    const input: ReminderInput = {
      title: v.title.trim(),
      body: v.body.trim(),
      audience: v.audience,
      ...(v.audience === 'GROUPS' ? { groups: v.groups } : {}),
    };
    const k = editing ? (editing.kind === 'CUSTOM' ? 'CUSTOM' : 'OFFSET') : v.kind;
    if (k === 'OFFSET') {
      const original = editing?.offsetMinutes ?? null;
      // Mốc cũ không tròn ngày mà admin không đổi số ngày -> giữ nguyên số phút.
      const keep = original !== null && original % 1440 !== 0 && v.days === Math.round(original / 1440);
      input.offsetMinutes = keep ? (original as number) : (v.days ?? 0) * 1440;
      input.sendTimeLocal = (v.time ?? dayjs('09:00', 'HH:mm')).format('HH:mm');
    } else {
      const iso = fromVnInput(v.at?.format(VN_FORMAT));
      if (!iso) {
        message.error('Chọn ngày giờ gửi hợp lệ.');
        return;
      }
      input.sendAt = iso;
    }
    setSaving(true);
    try {
      if (editing) await updateReminder(eventId, editing.id, input);
      else await createReminder(eventId, { ...input, kind: k });
      message.success(editing ? 'Đã lưu mốc nhắc.' : 'Đã thêm mốc nhắc.');
      await onSaved();
      onClose();
    } catch (e) {
      message.error(errorText(e, 'Không lưu được mốc nhắc.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? 'Sửa mốc nhắc' : 'Thêm mốc nhắc'}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Lưu"
      cancelText="Huỷ"
      confirmLoading={saving}
      destroyOnClose
      width={560}
    >
      <Form
        key={editing?.id ?? 'new'}
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={initialValues(editing)}
      >
        <Form.Item name="kind" label="Kiểu mốc">
          <Radio.Group disabled={!!editing}>
            <Radio value="OFFSET">Trước ngày diễn ra</Radio>
            <Radio value="CUSTOM">Ngày giờ cố định</Radio>
          </Radio.Group>
        </Form.Item>
        {kind === 'CUSTOM' ? (
          <Form.Item name="at" label="Gửi lúc (giờ Việt Nam)" rules={[{ required: true, message: 'Chọn ngày giờ gửi.' }]}>
            <DatePicker showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        ) : (
          <div className={styles.row2}>
            <Form.Item name="days" label="Trước (ngày)" rules={[{ required: true, message: 'Nhập số ngày.' }]}>
              <InputNumber min={0} max={3650} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="time" label="Giờ gửi (giờ Việt Nam)" rules={[{ required: true, message: 'Chọn giờ.' }]}>
              <TimePicker format="HH:mm" minuteStep={5} allowClear={false} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        )}
        <MessageFields groupOptions={groupOptions} />
      </Form>
    </Modal>
  );
};

interface SendNowModalProps {
  open: boolean;
  eventId: number;
  groupOptions: string[];
  onClose: () => void;
  onSent: () => void | Promise<void>;
}

/** "Gửi ngay": thông báo tức thì cho người có vé (hoặc theo nhóm); có hỏi lại trước khi gửi. */
export const SendNowModal = ({ open, eventId, groupOptions, onClose, onSent }: SendNowModalProps) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<{ title: string; body: string; audience: ReminderAudience; groups?: string[] }>();
  const [sending, setSending] = useState(false);

  const send = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    setSending(true);
    try {
      const res = await sendReminderNow(eventId, {
        title: v.title.trim(),
        body: v.body.trim(),
        audience: v.audience,
        ...(v.audience === 'GROUPS' ? { groups: v.groups } : {}),
      });
      message.success(`Đã gửi cho ${res?.sentCount ?? 0} người${res?.failCount ? `, lỗi ${res.failCount}` : ''}.`);
      await onSent();
      onClose();
    } catch (e) {
      message.error(errorText(e, 'Không gửi được thông báo.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title="Gửi thông báo ngay"
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Huỷ
        </Button>,
        <Popconfirm
          key="send"
          title="Gửi thông báo ngay?"
          description="Thông báo đi tức thì, không thu hồi được."
          okText="Gửi ngay"
          cancelText="Không"
          onConfirm={send}
        >
          <Button type="primary" loading={sending}>
            Gửi ngay
          </Button>
        </Popconfirm>,
      ]}
    >
      <p className={styles.hint}>Chỉ gửi được khi sự kiện đã Công bố.</p>
      <Form form={form} layout="vertical" preserve={false} initialValues={{ audience: 'TICKET' }}>
        <MessageFields groupOptions={groupOptions} />
      </Form>
    </Modal>
  );
};
